import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Building2, Shield, CheckCircle, ExternalLink, Copy, ArrowLeft, 
  RefreshCw, DollarSign, Lock, FileText, Wallet, TrendingUp, Info,
  Droplets, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contract addresses
const CONTRACTS = {
  BETH_TOKEN: "0x6822824c5349bf005e353ec5dda3fa34595ea811",
  COLLATERAL_VAULT: "0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d",
  CREDIT_LINE: "0x8abfe72db538c6ff99452a910b9bc7a980ef0fdd",
  LIQUIDATION_ENGINE: "0x65e67b63513de0b2bf463c47a2a284254e23fcda",
  TREASURY_MANAGER: "0xc21cd1629a88fe99dbf4cf5d202c04bd493cbf2c"
};

const TREASURY_WALLET = "0x2FB355B99b2060e42DB50D99393D00f47A41d322";

// Platform Reserve Info
const PLATFORM_RESERVE = {
  address: "0x8F01D20feaA323049976f35865F43D3C457F19CF",
  purpose: "Operational Reserve",
  status: "Active"
};

const TreasuryDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [oracleData, setOracleData] = useState(null);
  const [treasurySummary, setTreasurySummary] = useState(null);
  const [feeData, setFeeData] = useState({
    eth_balance: 0,
    beth_balance: 0,
    total_usd: 0
  });

  useEffect(() => {
    fetchData();
    fetchFeeData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [oracleRes, summaryRes] = await Promise.all([
        fetch(`${API}/oracle/beth-price`),
        fetch(`${API}/credit-enhancement/treasury-summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (oracleRes.ok) {
        setOracleData(await oracleRes.json());
      }
      if (summaryRes.ok) {
        setTreasurySummary(await summaryRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeData = async () => {
    try {
      const res = await fetch(`${API}/treasury/fee-balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setFeeData(await res.json());
      }
    } catch (error) {
      console.error('Error fetching fee data:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="treasury-dashboard">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Building2 className="w-6 h-6 text-[#b8860b]" />
                Treasury Management
              </h1>
              <p className="text-gray-400 text-sm">Smart contracts, pricing, and reserve management</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Status Banner */}
          <div className="p-3 rounded-lg flex items-center gap-3 bg-green-900/30 border border-green-500/30">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-400">Platform Active</span>
            <span className="text-gray-400 text-sm">Internal pricing at ${oracleData?.price_usd || '1.00'}/BETH</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Treasury Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-[#b8860b]/20 to-[#b8860b]/5 border-[#b8860b]/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-[#b8860b]" />
                <p className="text-gray-400 text-sm">BETH Price</p>
              </div>
              <p className="text-2xl font-bold text-white">${oracleData?.price_usd || '1.00'}</p>
              <p className="text-xs text-gray-500 mt-1">Internal treasury pricing</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Lock className="w-5 h-5 text-blue-400" />
                <p className="text-gray-400 text-sm">Total Reserve</p>
              </div>
              <p className="text-2xl font-bold text-white">1B BETH</p>
              <p className="text-xs text-gray-500 mt-1">1 Billion tokens allocated</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <p className="text-gray-400 text-sm">Committed</p>
              </div>
              <p className="text-2xl font-bold text-green-400">
                {formatNumber(treasurySummary?.total_committed_beth || 0)} BETH
              </p>
              <p className="text-xs text-gray-500 mt-1">In credit enhancements</p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-purple-400" />
                <p className="text-gray-400 text-sm">Active Guarantees</p>
              </div>
              <p className="text-2xl font-bold text-white">{treasurySummary?.total_guarantees || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Outstanding commitments</p>
            </CardContent>
          </Card>
        </div>

        {/* Fee Balance Tracking */}
        <Card className="bg-gradient-to-br from-green-900/20 to-green-900/5 border-green-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Collected Fees
            </CardTitle>
            <CardDescription>Real-time fee balance in treasury wallet</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">ETH Balance</p>
                <p className="text-2xl font-bold text-white">{feeData.eth_balance?.toFixed(4) || '0.0000'} ETH</p>
                <p className="text-sm text-green-400">${feeData.eth_usd?.toLocaleString() || '0.00'} USD</p>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">ETH Price</p>
                <p className="text-2xl font-bold text-white">${feeData.eth_price?.toLocaleString() || '3,500'}</p>
                <p className="text-sm text-gray-400">Live price</p>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">BETH Price</p>
                <p className="text-2xl font-bold text-white">${feeData.beth_price?.toFixed(2) || '1.00'}</p>
                <p className="text-sm text-gray-400">Internal pricing</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchFeeData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Balance
              </Button>
              <Link to="/fee-conversion" className="flex-1">
                <Button className="w-full bg-green-600 hover:bg-green-700">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Convert to USD
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Treasury Wallet */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#b8860b]" />
              Treasury Wallet
            </CardTitle>
            <CardDescription>Primary wallet holding BETH reserve</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-[#1a2d4a]/50 rounded-lg">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Wallet Address</p>
                <p className="text-white font-mono">{TREASURY_WALLET}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(TREASURY_WALLET, 'Address')}>
                <Copy className="w-4 h-4" />
              </Button>
              <a href={`https://etherscan.io/address/${TREASURY_WALLET}`} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Deployed Contracts */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#b8860b]" />
              Deployed Smart Contracts (Ethereum Mainnet)
            </CardTitle>
            <CardDescription>All contracts are live and operational</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(CONTRACTS).map(([name, address]) => (
                <div key={name} className="flex items-center justify-between p-3 bg-[#1a2d4a]/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{name.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-500 font-mono">{address}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(address, name)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <a href={`https://etherscan.io/address/${address}#code`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" className="text-[#b8860b]">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Verification Guide */}
            <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium mb-1">Contract Verification</p>
                  <p className="text-sm text-gray-400 mb-3">
                    Verify contracts on Etherscan to make source code public and build trust.
                  </p>
                  <Link to="/contract-verification">
                    <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                      <FileText className="w-4 h-4 mr-2" />
                      Verification Guide
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Platform Subscription Info */}
        <Card className="bg-gradient-to-br from-[#b8860b]/20 to-amber-900/20 border-[#b8860b]/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Droplets className="w-5 h-5 text-[#b8860b]" />
              BETH Platform Credits
            </CardTitle>
            <CardDescription>Internal credit system for platform access</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">BETH Price</p>
                <p className="text-white font-bold">$1.00 USD</p>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Model</p>
                <p className="text-white font-bold">Fixed Price</p>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Status</p>
                <p className="text-green-400 font-bold flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Active
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-[#1a2d4a]/50 rounded-lg mb-3">
              <p className="text-sm text-gray-300 mb-2">
                <strong>Non-Custodial Payment Model:</strong>
              </p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• USD payments processed via Stripe (platform never touches USD)</li>
                <li>• BETH credits allocated to internal account upon payment</li>
                <li>• Credits used for platform access and tool usage</li>
                <li>• No trading, no speculation, clean compliance</li>
              </ul>
            </div>
            
            <div className="flex gap-2">
              <Button 
                className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                onClick={() => window.location.href = '/subscribe'}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Subscribe to Platform
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => window.location.href = '/credit-enhancement'}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Access Tools
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Model */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#b8860b]" />
              Pricing Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Pricing Source</p>
                <p className="text-white font-bold">Internal Treasury</p>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Current Price</p>
                <p className="text-[#b8860b] font-bold">${oracleData?.price_usd || '1.00'} USD</p>
              </div>
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">DEX Required</p>
                <p className="text-white font-bold">No</p>
              </div>
            </div>
            
            <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium mb-1">Internal Pricing Model</p>
                  <p className="text-sm text-gray-400">
                    BETH uses internal treasury pricing for compliance-safe collateral valuation. 
                    No external DEX or liquidity pool is required for platform operation. 
                    The price is set by treasury management and can be adjusted as needed.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/lending-reserve">
                <Button className="w-full bg-[#b8860b] hover:bg-[#9a7209]">
                  <Lock className="w-4 h-4 mr-2" />
                  Manage BETH Reserve
                </Button>
              </Link>
              <Link to="/credit-enhancement">
                <Button variant="outline" className="w-full border-[#b8860b] text-[#b8860b] hover:bg-[#b8860b]/10">
                  <FileText className="w-4 h-4 mr-2" />
                  Credit Enhancement
                </Button>
              </Link>
              <Link to="/transactions">
                <Button variant="outline" className="w-full">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  View Transactions
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Notice */}
        <Card className="bg-green-900/20 border-green-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Compliance Framework
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">BETH is a utility token, NOT a security</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">Non-custodial architecture - users control their assets</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">Accredited investors only (SEC Rule 501 compliant)</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-300">Internal pricing for compliance-safe operations</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TreasuryDashboard;
